import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { createPaymentInTicket, createPaymentOutTicket } from "../../api/tickets";
import { getCashDrawer } from "../../api/cashDrawer";
import { formatApiError } from "../../utils/formatApiError";
import { useAuth } from "../../context/AuthContext";
import { FIELD_CLASS, LABEL_TEXT } from "../../ui/modalStyles";
import SBINModal from "./SBINModal";

const titleMap = {
  in: "New Payment In Ticket",
  out: "New Payment Out Ticket",
};

function formatDateForSalon(date, timeZone) {
  if (!date) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const values = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(values, part.type)) {
          values[part.type] = part.value;
        }
      }
      if (values.year && values.month && values.day) {
        return `${values.year}-${values.month}-${values.day}`;
      }
    } catch {
      // Fall back to browser local date when timezone formatting fails.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function PaymentInOutModal({ isOpen, mode, onClose }) {
  const { user, salonTimezone } = useAuth();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [amountError, setAmountError] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [availableOut, setAvailableOut] = useState(null);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState("");
  const createButtonClass =
    "rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60";
  const payoutDate = useMemo(
    () => formatDateForSalon(new Date(), salonTimezone),
    [salonTimezone]
  );

  const salonId =
    user?.salon_id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null);

  const amountNumber = useMemo(() => Number(amount || 0), [amount]);
  const amountValid = Number.isFinite(amountNumber) && amountNumber > 0;

  useEffect(() => {
    if (!isOpen) return;
    setAmount("");
    setReason("");
    setShowValidation(false);
    setSubmitError("");
    setAmountError("");
    setReasonError("");
    setAvailableError("");
    if (mode !== "out") {
      setAvailableOut(null);
      return;
    }
    if (!salonId) return;
    let isActive = true;
    const loadAvailable = async () => {
      setAvailableLoading(true);
      try {
        const data = await getCashDrawer({ date: payoutDate, salon_id: salonId });
        if (!isActive) return;
        const beginCash = Number(data?.begin_amount ?? 0);
        const paidIn = Number(data?.paid_in ?? 0);
        const tendered = Number(data?.tendered ?? 0);
        const paidOut = Number(data?.paid_out ?? 0);
        const available = beginCash + paidIn - Math.abs(paidOut) + tendered;
        setAvailableOut(Number.isFinite(available) ? available : null);
      } catch (err) {
        console.error(err);
        if (!isActive) return;
        setAvailableOut(null);
        setAvailableError(formatApiError(err));
      } finally {
        if (isActive) {
          setAvailableLoading(false);
        }
      }
    };
    loadAvailable();
    return () => {
      isActive = false;
    };
  }, [isOpen, mode, payoutDate, salonId]);

  const handleSubmit = async () => {
    setShowValidation(true);
    const trimmedReason = (reason || "").trim();
    const reasonValid = trimmedReason.length > 0;
    if (!amountValid || !mode || !reasonValid) {
      if (!reasonValid) {
        setReasonError("Reason is required.");
      }
      return;
    }
    if (amountExceedsAvailable) return;
    if (!salonId) {
      setSubmitError("Salon context is missing. Please sign in again.");
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");
    setAmountError("");
    setReasonError("");
    try {
      const payload = {
        salon_id: Number(salonId),
        amount: Number(amount),
        reason: trimmedReason,
      };
      const dateValue = String(payoutDate || "").trim();
      if (dateValue !== "") {
        payload.date = dateValue;
      }
      let createdTicket = null;
      if (mode === "in") {
        createdTicket = await createPaymentInTicket(payload);
      } else {
        createdTicket = await createPaymentOutTicket(payload);
      }
      onClose();
      if (createdTicket) {
        window.dispatchEvent(
          new CustomEvent("tickets:created", { detail: createdTicket })
        );
      }
      window.dispatchEvent(
        new CustomEvent("cashDrawer:refresh", {
          detail: { salonId, date: payoutDate },
        })
      );
      window.dispatchEvent(new Event("tickets:refresh"));
    } catch (err) {
      console.error(
        "Payment in/out error:",
        err?.response?.status,
        err?.response?.data
      );
      const data = err?.response?.data;
      if (data?.available != null) {
        const detail =
          data?.detail ||
          "Not enough cash available for payout.";
        const beginCash = Number(data?.begin_cash ?? 0);
        const paidInSoFar = Number(data?.paid_in_so_far ?? 0);
        const tendered = Number(data?.tendered_so_far ?? 0);
        const paidOutSoFar = Number(data?.paid_out_so_far ?? 0);
        const available = Number(data?.available ?? 0);
        const message = `${detail} Maximum you can pay out right now is $${available.toFixed(
          2
        )} (Begin Cash $${beginCash.toFixed(2)} + Paid In $${paidInSoFar.toFixed(
          2
        )} + Tendered $${tendered.toFixed(2)} - Paid Out $${paidOutSoFar.toFixed(
          2
        )}).`;
        setAmountError(message);
        setAvailableOut(available);
        return;
      }
      setSubmitError(formatApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const amountExceedsAvailable =
    mode === "out" &&
    availableOut != null &&
    Number.isFinite(availableOut) &&
    amountValid &&
    amountNumber > availableOut;

  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onClose}
      title={titleMap[mode] || "New Payment Ticket"}
      size="md"
      fitContent={true}
      panelClassName="sm:max-w-[600px]"
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || amountExceedsAvailable}
            className={createButtonClass}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {submitError && (
          <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {submitError}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
          <label className={`${LABEL_TEXT} sm:w-36 sm:pt-2`}>
            Amount
          </label>
          <div className="flex-1">
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className={`${FIELD_CLASS} w-full`}
              placeholder="0.00"
            />
            {showValidation && !amountValid && (
              <p className="mt-1 text-xs text-rose-600">
                Amount must be greater than 0.
              </p>
            )}
            {amountExceedsAvailable && (
              <p className="mt-1 text-xs text-rose-600">
                Not enough cash available. Maximum you can pay out right now is ${availableOut.toFixed(
                  2
                )}.
              </p>
            )}
            {amountError && (
              <p className="mt-1 text-xs text-rose-600">
                {amountError}
              </p>
            )}
            {mode === "out" && !availableError && availableOut != null && (
              <p className="mt-1 text-xs text-slate-500">
                Available to pay out today: ${availableOut.toFixed(2)}
              </p>
            )}
            {mode === "out" && availableLoading && (
              <p className="mt-1 text-xs text-slate-500">
                Loading available payout amount...
              </p>
            )}
            {mode === "out" && availableError && (
              <p className="mt-1 text-xs text-rose-600">
                {availableError}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
          <label className={`${LABEL_TEXT} sm:w-36 sm:pt-2`}>
            Reason
          </label>
          <div className="flex-1">
            <textarea
              rows={3}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setReasonError("");
              }}
              className={`${FIELD_CLASS} h-auto w-full py-3`}
              placeholder="Reason"
            />
            {showValidation && reasonError && (
              <p className="mt-1 text-xs text-rose-600">
                {reasonError}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
          <div className={`${LABEL_TEXT} sm:w-36 sm:pt-1.5`}>
            Payment Method
          </div>
          <div className="flex-1">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="radio" checked readOnly />
              Cash
            </label>
          </div>
        </div>
      </div>
    </SBINModal>
  );
}

PaymentInOutModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["in", "out"]),
  onClose: PropTypes.func.isRequired,
};
